import React, { useState, useCallback } from "react"
import { connect } from "react-redux"
import { formValueSelector } from "redux-form/immutable"
import regexes from "shared/imports/regex"

import { AddressField } from "shared-web/forms/fields/AddressField"
import { fetchOfficialsByAddress } from "QuorumGrassroots/services"
import { fieldWithOfficialActions } from "QuorumGrassroots/campaign-forms/action-creators"
import { useDebouncedCallback } from "QuorumGrassroots/framework/hooks/useDebouncedCallback"

import {
    Address,
    Campaign,
    Official,
    SupporterRegisterFields,
} from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/interfaces"
import { parseAddress } from "QuorumGrassroots/widgets/ReusableComponents/UserInfoFormSection/helper"
import { mapBackendErrorsToFormFields } from "QuorumGrassroots/widgets/action-creators"

/**
 * Extracts error data from different error types (ResponseNotOkError or AxiosError)
 * Handles the backend's unusual format: ['{"address": "..."}']
 */
const extractErrorData = async (error: any): Promise<any> => {
    // Handle already converted error (from fetchOfficialsByAddress)
    if (error.response?.data) {
        return error.response.data
    }

    // Handle ResponseNotOkError from fetch API
    if (error.response instanceof Response) {
        try {
            return await error.response.clone().json()
        } catch {
            const errorText = await error.response.clone().text()

            if (errorText.startsWith("['") && errorText.endsWith("']")) {
                const jsonString = errorText.slice(2, -2)
                return JSON.parse(jsonString)
            }

            return JSON.parse(errorText)
        }
    }

    return null
}

interface Props {
    email: string
    campaign: Campaign
    uniqueWidgetId: string
    updateCanParticipate: (uniqueWidgetId: string, canParticipate: boolean) => void
    setOfficialsPreview: (uniqueWidgetId: string, officials: Official[]) => void
    setOfficialsLoading: (uniqueWidgetId: string, isLoading: boolean) => void
    setTargetedMessages: (uniqueWidgetId: string, messages: Record<string, unknown>) => void
    storeSupporterPlaceholderValues: (uniqueWidgetId: string, supporter: SupporterRegisterFields) => void
    replaceTargetedMessageSupporterNamePlaceholders: (
        uniqueWidgetId: string,
        supporter: SupporterRegisterFields,
    ) => void
    name: string
    dataCy?: string
    label?: string
    placeholder?: string
    displayErrorWithoutTouch?: boolean
    validate?: (formValue: unknown) => string
    showAsterisk?: boolean
    shouldGeocode?: boolean
    accessibilityId?: string
    isCampaignPage?: boolean
    disabled?: boolean
    tooltipText?: string | null
    firstname: string
    lastname: string
    organization: string
    IconComponent?: React.ComponentType
    "aria-required"?: string | boolean
}

export const UnconnectedAddressFieldWithOfficials = (props: Props) => {
    const { validate } = props
    const [isFetchingOfficialsByAddress, setIsFetchingOfficialsByAddress] = useState(false)
    const [addressError, setAddressError] = useState<string | null>(null)

    const handleOfficialsByAddress = useDebouncedCallback((address: string | Address) => {
        const { addressDict, addressText, isInvalidAddress } = parseAddress(address)
        if (isInvalidAddress) return

        const isEmailValid = regexes.emailValidationRegex.test(props.email)
        if (!isEmailValid) return

        // Clear any previous address error and reset state
        setAddressError(null)
        props.updateCanParticipate(props.uniqueWidgetId, false)
        props.setOfficialsPreview(props.uniqueWidgetId, null)
        props.setOfficialsLoading(props.uniqueWidgetId, true)
        setIsFetchingOfficialsByAddress(true)
        fetchOfficialsByAddress(props.campaign.id, addressText, props.email, addressDict)
            .then((response) => {
                const { targets, targeted_messages } = response
                const supporter = {
                    firstname: props.firstname,
                    lastname: props.lastname,
                    address: addressText,
                    email: props.email,
                    organization: props.organization,
                }
                props.updateCanParticipate(props.uniqueWidgetId, true)
                props.setTargetedMessages(props.uniqueWidgetId, targeted_messages)
                props.replaceTargetedMessageSupporterNamePlaceholders(props.uniqueWidgetId, supporter)
                props.storeSupporterPlaceholderValues(props.uniqueWidgetId, supporter)
                props.setOfficialsPreview(
                    props.uniqueWidgetId,
                    targets.map((official: Official) => ({
                        value: String(official.id),
                        label: official.name,
                        imageUrl: official.image_url,
                        targeted: true,
                        message_group_id: String(official.message_group_id),
                    })),
                )
            })
            .catch(async (error) => {
                try {
                    const errorData = await extractErrorData(error)
                    if (errorData) {
                        const mappedErrors = mapBackendErrorsToFormFields(errorData)
                        if (mappedErrors.input_address) {
                            setAddressError(mappedErrors.input_address)
                        }
                        return
                    }
                } catch {}

                setAddressError("Error validating address. Please try again.")
            })
            .finally(() => {
                setIsFetchingOfficialsByAddress(false)
                props.setOfficialsLoading(props.uniqueWidgetId, false)
            })
    }, 800)

    const customValidate = useCallback(
        (value: any) => {
            if (addressError) {
                return addressError
            }

            return validate ? validate(value) : undefined
        },
        [addressError, validate],
    )

    const handleBlur = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            handleOfficialsByAddress(e.target.value)
        },
        [handleOfficialsByAddress],
    )

    const handleClickAddressSuggestion = useCallback(
        (address: Address | string) => {
            handleOfficialsByAddress(address)
        },
        [handleOfficialsByAddress],
    )

    return (
        <>
            <AddressField
                onBlur={handleBlur}
                onClickAddressSuggestion={handleClickAddressSuggestion}
                name={props.name}
                dataCy={props.dataCy}
                label={props.label}
                placeholder={props.placeholder}
                displayErrorWithoutTouch={props.displayErrorWithoutTouch || !!addressError}
                validate={customValidate}
                showAsterisk={props.showAsterisk}
                shouldGeocode={props.shouldGeocode}
                accessibilityId={props.accessibilityId}
                isCampaignPage={props.isCampaignPage}
                disabled={props.disabled || isFetchingOfficialsByAddress}
                tooltipText={props.tooltipText}
                IconComponent={props?.IconComponent}
                aria-required={props["aria-required"]}
            />
        </>
    )
}

const selector = formValueSelector("registration")

const mapStateToProps = (state) => {
    return {
        email: selector(state, "email"),
        firstname: selector(state, "firstname"),
        lastname: selector(state, "lastname"),
        organization: selector(state, "public_organization_name"),
    }
}

export const AddressFieldWithOfficials = connect(
    mapStateToProps,
    fieldWithOfficialActions,
)(UnconnectedAddressFieldWithOfficials)
